import { useLocation, useNavigate } from '@tanstack/react-router'
import { useEffect, useState } from 'react'

import { TrialEmailAliasError } from '@/modules/errors'
import { useForm } from '@/stores'
import { ACCOUNT_TYPES } from '../components/AccountTypeToggle'
import { useAuthMutations } from '../stores'
import { isEmailAlias } from '../utils/emailAddress'
import {
  type TrialSignUpDetails,
  toTrialStartSearch,
} from '../utils/onboarding'
import { readTrialSource, type TrialSource } from '../utils/trialSource'
import { useStartTrial } from './useStartTrial'

type TrialFormData = ReturnType<typeof useForm>['data']
type SetFieldValue = ReturnType<typeof useForm>['setFieldValue']

const splitFullName = (fullName: string | null) => {
  const [firstName = '', ...lastNames] = (fullName ?? '').trim().split(/\s+/)
  return { firstName, lastName: lastNames.join(' ') }
}

const toTrialDetails = (
  data: TrialFormData,
  trialSource: TrialSource,
): TrialSignUpDetails => {
  const isIndividual = data.accountType === ACCOUNT_TYPES.individual
  const firstName = (data.firstName as string) ?? ''
  const lastName = (data.lastName as string) ?? ''
  const fullName = `${firstName} ${lastName}`.trim()
  return {
    name: isIndividual ? fullName : ((data.accountName as string) ?? ''),
    firstName,
    lastName,
    // A role picked before switching to Individual is hidden, so it is not sent.
    role: isIndividual ? undefined : (data.role as string) || undefined,
    trialSource,
  }
}

// Outreach links carry name= and email=. The inputs are controlled, so the store must hold them.
const usePrefilledFields = (
  searchParams: URLSearchParams,
  setFieldValue: SetFieldValue,
) => {
  useEffect(() => {
    const { firstName, lastName } = splitFullName(searchParams.get('name'))
    const email = searchParams.get('email')
    if (firstName) setFieldValue('firstName', firstName)
    if (lastName) setFieldValue('lastName', lastName)
    if (email) setFieldValue('email', email)
  }, [searchParams, setFieldValue])
}

type TrialForm = ReturnType<typeof useForm>

const useTrialCode = ({ data, setFieldError }: TrialForm) => {
  const { signUp } = useAuthMutations()
  const [isEnteringCode, setIsEnteringCode] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const sendCode = () =>
    signUp({
      email: data.email as string,
      metadata: { firstName: data.firstName, lastName: data.lastName },
    })

  const submitDetails = (event: React.FormEvent) => {
    event.preventDefault()
    setError(null)
    setFieldError('email', undefined)
    if (isEmailAlias((data.email as string) ?? '')) {
      setFieldError('email', new TrialEmailAliasError().message)
      return
    }
    sendCode()
      .then(() => setIsEnteringCode(true))
      .catch((failure: Error) => setError(failure.message))
  }

  const editDetails = () => setIsEnteringCode(false)

  return { error, isEnteringCode, sendCode, submitDetails, editDetails }
}

export const useTrialSignUp = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const [searchParams] = useState(() => new URLSearchParams(location.searchStr))
  const [trialSource] = useState(() => readTrialSource(searchParams))
  const form = useForm()
  const code = useTrialCode(form)
  usePrefilledFields(searchParams, form.setFieldValue)

  const details = toTrialDetails(form.data, trialSource)
  const showEmailError = (message: string) => {
    code.editDetails()
    form.setFieldError('email', message)
  }
  const { startTrial, isAlreadyMember } = useStartTrial({
    onEmailRefused: showEmailError,
    onUnexpectedFailure: () =>
      navigate({ to: '/trial/add-card', search: toTrialStartSearch(details) }),
  })

  return {
    ...code,
    form,
    isAlreadyMember,
    isEnteringCode: code.isEnteringCode && !isAlreadyMember,
    prefilledFirstName: splitFullName(searchParams.get('name')).firstName,
    finishSignUp: () => startTrial(details),
  }
}

export type TrialSignUp = ReturnType<typeof useTrialSignUp>
